import { Fragment, useEffect, useLayoutEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  GlassCard,
  HelpAnchor,
  Pill,
  ScrollCue,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePageTabs,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { cn } from '@/shared/cn';
import { SURFACE_PAGE_TITLE_LARGE } from '@/shared/ui-class';
import { vizToneColor } from '@/versions/v1/modules/_viz';
import { ActorChip } from '../advisory/PipelineStrip';
import { CARD_TABLE, CARD_TABLE_DENSE, DowntimeCard, RankBar } from '../downtime/DowntimeCard';
import { PerfBand, PerfTab, REF_CHIP } from './PerfTab';
import { NextDayPlan } from './NextDayPlan';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import { Commentable } from '@/shared/comments/Commentable';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import type { CCCCase, ThreeWEntry } from './seed';
import {
  PRIORITY_LABEL,
  STATUS_LABEL,
  STATUS_TONE,
  cccReview,
  formatCommitDate,
  handoverCrew,
  handoverForShift,
  meetingCopy,
  paretoByArea,
  previousThreeWs,
  registerByArea,
  rootCauseReviewerLabel,
  threeWPriority,
  threeWStatus,
} from './Scene.transform';
import {
  cccBand,
  controlCheckTally,
  isMeetingTab,
  meetingHeaderFigures,
  meetingTabHeadlines,
  previousDueCount,
  registerRankCounts,
  type HeaderFigure,
} from './MeetingBoard.transform';

type MeetingTab = 'register' | 'perf' | 'next-24h' | 'shift-handover' | 'ccc-review';

/** The drill-downs under the board (locked comp `meeting.html` B), the
 * register first: the meeting's opening read now sits at the page top, so the
 * first tab is Step 3's output (polish.P12 adjudication 1 — every persona
 * lands here; the persona picker is a lens, not navigation). */
const TABS: { id: MeetingTab; label: string }[] = [
  { id: 'register', label: 'Next 24 hours' },
  { id: 'perf', label: 'Measured performance' },
  { id: 'next-24h', label: 'Next 24 h plan' },
  { id: 'shift-handover', label: 'Shift handover' },
  { id: 'ccc-review', label: 'CCC review' },
];

/** Chips and pills at the comp's size (DESIGN.md § Type ramp: 10.5px → `text-[10px]` 600). */
const PILL_SM = 'whitespace-nowrap px-2 py-px text-[10px] font-semibold tracking-[0.05em]';

export function DailyMeetingScene() {
  const { projectId, tab: tabSegment } = useParams<{ projectId: string; tab?: string }>();
  const project = projectTileBySlug(projectId);
  const { state } = useScenario();
  const copy = meetingCopy();
  const [tab, setTab] = useState<MeetingTab>('register');
  // A `/meeting/<tab>` deep link (Advisory's path-segment idiom) opens that
  // view before first paint; the plain route keeps the register default
  // (polish.P13 adjudication 5: the Ops supervisor's carried-in card).
  useLayoutEffect(() => {
    if (isMeetingTab(tabSegment)) setTab(tabSegment);
  }, [tabSegment]);
  const headlines = meetingTabHeadlines(state);
  const tabItems = TABS.map((item) => ({ ...item, count: headlines[item.id] }));

  useEffect(() => {
    document.title = project ? `${copy.title} · ${project.name}` : `${copy.title} · OptiFlow`;
  }, [copy.title, project]);

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Plant not found" />
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  return (
    <SurfacePageLayout width="full">
      <Commentable name="DailyMeetingScene">
        <SurfacePageBody className="space-y-2.5" width="full">
          <MeetingHeader title={copy.title} figures={meetingHeaderFigures(state)} />

          <Commentable name="MeetingBoard">
            <div className="space-y-2.5">
              <PerfBand />
              <MeetingBoardRow />
            </div>
          </Commentable>

          <SurfacePageTabs aria-label="Meeting" items={tabItems} value={tab} onChange={setTab} variant="ink" />

          {tab === 'register' ? (
            <Commentable name="ThreeWRegister">
              <ThreeWRegisterTable areas={registerByArea()} />
            </Commentable>
          ) : null}
          {tab === 'perf' ? <PerfTab /> : null}
          {tab === 'next-24h' ? <NextDayPlan /> : null}
          {tab === 'shift-handover' ? (
            <Commentable name="ShiftHandover">
              <ShiftHandoverTab />
            </Commentable>
          ) : null}
          {tab === 'ccc-review' ? (
            <Commentable name="CccReview">
              <CccReviewTab />
            </Commentable>
          ) : null}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

// --- Header ----------------------------------------------------------------

/** The page's one header row (comp `.title-row`): the title, then the meeting
 * itself as header figures — each figure over its comparison, the abnormal
 * one (control verification under its target) in the warning tone. */
function MeetingHeader({ title, figures }: { title: string; figures: readonly HeaderFigure[] }) {
  return (
    <header className="flex flex-wrap items-end gap-x-6 gap-y-3">
      <h1 className={cn(SURFACE_PAGE_TITLE_LARGE, 'mr-auto min-w-0')}>{title}</h1>
      <div className="flex items-center gap-3">
        <dl className="flex flex-wrap items-stretch tabular-nums" aria-label="Today’s meeting">
          {figures.map((figure, index) => (
            <div
              key={figure.id}
              data-header-figure={figure.id}
              className={cn('min-w-0 px-3.5', index === 0 ? 'pl-0' : 'border-l border-[var(--hairline-strong)]')}
            >
              <dt className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.07em] text-muted">{figure.label}</dt>
              <dd
                className={cn(
                  'whitespace-nowrap text-lg font-semibold leading-snug',
                  figure.tone === 'warn' ? 'text-[var(--state-warning)]' : 'text-ink-primary',
                )}
              >
                {figure.value}
              </dd>
              <dd className="whitespace-nowrap text-xs text-muted">
                {figure.pre}
                {figure.lead ? <span className="font-semibold text-ink-secondary">{figure.lead}</span> : null}
                {figure.rest}
              </dd>
            </div>
          ))}
        </dl>
        <HelpAnchor doc="meeting/overview" label="Daily meeting" />
      </div>
    </header>
  );
}

// --- The board row: Step 1 beside the 30-day pattern -------------------------

/** One word, one tone: the bucket pill reads the register's own status tone
 * for the same word (Slipped is neutral on both panels). */
const BUCKET_TONE = {
  done: STATUS_TONE['done-awaiting-close'],
  slipped: STATUS_TONE.slipped,
  overdue: STATUS_TONE.overdue,
} as const;

function MeetingBoardRow() {
  const previous = previousThreeWs();
  const pareto = paretoByArea();
  const top = pareto[0]?.count ?? 1;

  return (
    <div className="grid min-w-0 gap-3 xl:grid-cols-12 xl:items-start min-[1280px]:items-stretch">
      <DowntimeCard
        title="Previous 3Ws"
        meta={
          <>
            due since the last meeting · <span className="font-semibold text-ink-secondary">{previousDueCount(previous)}</span>
          </>
        }
        className="xl:col-span-8"
      >
        <div className="grid min-w-0 gap-y-2 min-[1280px]:grid-cols-[1.22fr_1fr_1fr] min-[1280px]:gap-y-0">
          {previous.done.length > 0 ? (
            <PreviousBucket label="Done — awaiting close" tone={BUCKET_TONE.done} rows={previous.done} showClose />
          ) : null}
          {previous.slipped.length > 0 ? (
            <PreviousBucket label="Slipped" tone={BUCKET_TONE.slipped} rows={previous.slipped} />
          ) : null}
          {previous.overdue.length > 0 ? (
            <PreviousBucket label="Overdue" tone={BUCKET_TONE.overdue} rows={previous.overdue} />
          ) : null}
        </div>
      </DowntimeCard>

      <DowntimeCard
        title="Recurring pattern check"
        meta="3W rows by area · rolling 30 days"
        className="flex flex-col xl:col-span-4"
      >
        <ol className="flex flex-1 flex-col justify-evenly gap-2 tabular-nums">
          {pareto.map((row, index) => (
            <li key={row.area} className="grid grid-cols-[9.5rem_minmax(0,1fr)_1.75rem] items-center gap-3 text-[13px]">
              <span className={cn('truncate text-ink-primary', index === 0 ? 'font-semibold' : 'font-medium')}>{row.area}</span>
              <RankBar ratio={row.count / top} tone={index === 0 ? 'top' : 'ink'} className="h-2" />
              <span className="text-right font-semibold text-ink-primary">{row.count}</span>
            </li>
          ))}
        </ol>
      </DowntimeCard>
    </div>
  );
}

/** One Step 1 bucket. At 1280 px and wider the three buckets sit side by side
 * as the comp's three-column board (id and owner over a two-line issue);
 * narrower, they stack as C's one-line list (id · issue · owner), so the
 * issue never wraps to three lines in a narrow column. */
function PreviousBucket({
  label,
  tone,
  rows,
  showClose,
}: {
  label: string;
  tone: 'default' | 'danger' | 'success' | 'warning';
  rows: readonly ThreeWEntry[];
  showClose?: boolean;
}) {
  return (
    <section
      aria-label={label}
      className="min-w-0 min-[1280px]:border-l min-[1280px]:border-[var(--hairline)] min-[1280px]:px-3 min-[1280px]:first:border-l-0 min-[1280px]:first:pl-0 min-[1280px]:last:pr-0"
    >
      <div className="flex items-center gap-2 border-b border-divider pb-1">
        <Pill tone={tone} className={PILL_SM}>
          {label}
        </Pill>
        <span className="ml-auto text-[13px] font-semibold tabular-nums text-ink-primary">{rows.length}</span>
      </div>
      <ul>
        {rows.map((row) => (
          <li
            key={row.id}
            className="grid grid-cols-[3.5rem_minmax(0,1fr)_auto_auto] items-center gap-x-2.5 border-b border-divider py-1.5 last:border-b-0 min-[1280px]:grid-cols-[auto_auto_minmax(0,1fr)_auto] min-[1280px]:gap-x-2 min-[1280px]:gap-y-0 min-[1280px]:py-[2px]"
          >
            <span className="whitespace-nowrap font-mono text-xs text-muted min-[1280px]:col-start-1 min-[1280px]:row-start-1">
              {row.id}
            </span>
            <span
              title={row.issue}
              className="min-w-0 truncate text-[13px] text-ink-primary min-[1280px]:col-span-4 min-[1280px]:text-[12.5px] min-[1280px]:row-start-2 min-[1280px]:line-clamp-2 min-[1280px]:whitespace-normal min-[1280px]:leading-[1.3]"
            >
              {row.issue}
            </span>
            <span className="min-[1280px]:col-start-2 min-[1280px]:row-start-1">
              <ActorChip actor="Human">{row.who}</ActorChip>
            </span>
            {showClose ? (
              <button
                type="button"
                disabled
                className="whitespace-nowrap rounded-full border border-[var(--hairline-strong)] px-2 py-px text-[10px] font-semibold text-muted disabled:cursor-default min-[1280px]:col-start-4 min-[1280px]:row-start-1"
              >
                Close in meeting
              </button>
            ) : (
              <span />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

// --- The 3W register (Step 3) ------------------------------------------------

function Tick({ on }: { on: boolean }) {
  return <span className={on ? 'font-semibold text-ink-primary' : 'text-muted'}>{on ? '✓' : '—'}</span>;
}

function EscalatedCell({ row }: { row: ThreeWEntry }) {
  return <Tick on={row.escalated} />;
}

/** The CCC id as a neutral reference: the Critical pill already carries the
 * row's state (comp `.ref`). */
function CccCell({ row }: { row: ThreeWEntry }) {
  if (!row.cccCaseId) return <span className="text-muted">—</span>;
  return <span className={REF_CHIP}>{row.cccCaseId}</span>;
}

/** Safety · Production · Resources as one tick trio (comp `.spr`): a lit
 * letter where the row carries the tick, a dashed ghost where it does not. */
function SprTicks({ row }: { row: ThreeWEntry }) {
  const cells: [string, boolean][] = [
    ['S', row.safety],
    ['P', row.production],
    ['R', row.resources],
  ];
  return (
    <span className="inline-grid grid-cols-3 gap-0.5 text-center text-[10px] font-semibold" aria-label="Safety, production, resources">
      {cells.map(([letter, on]) => (
        <span
          key={letter}
          aria-label={`${letter} ${on ? 'yes' : 'no'}`}
          className={cn(
            'w-4 rounded py-px',
            on
              ? 'border border-[var(--hairline-strong)] bg-[var(--surface-1)] text-ink-primary'
              : 'border border-dashed border-[var(--hairline-strong)] text-muted',
          )}
        >
          {letter}
        </span>
      ))}
    </span>
  );
}

/** The priority pill — the two ranks the room acts on first, no pill for the
 * rest. Derived from the sort rank, so it can never disagree with the order. */
function PriorityCell({ row }: { row: ThreeWEntry }) {
  const priority = threeWPriority(row);
  if (priority === 'standard') return null;
  return (
    <Pill tone={priority === 'critical' ? 'danger' : 'warning'} className={PILL_SM}>
      {PRIORITY_LABEL[priority]}
    </Pill>
  );
}

/** The status pill, under the commit dates. Derived and inert — no control
 * (K4: no write path this pass). */
function CommitStatus({ row }: { row: ThreeWEntry }) {
  const status = threeWStatus(row);
  return (
    <span className="mt-1 block">
      <Pill tone={STATUS_TONE[status]} className={PILL_SM}>
        {STATUS_LABEL[status]}
      </Pill>
    </span>
  );
}

/** The issue cell (comp `.reg`): the priority pill and id line, the issue in
 * ink, the preventive task beneath it, and on the rows the register ranks
 * critical or high the fallback task, labelled — What answers the issue it
 * sits under, full text, no truncated column. */
function IssueCell({ row }: { row: ThreeWEntry }) {
  return (
    <TD className="min-w-[22rem] align-top">
      <span className="mb-0.5 flex flex-wrap items-center gap-1.5">
        <PriorityCell row={row} />
        <span className="whitespace-nowrap font-mono text-xs text-muted">
          {row.id}
          {row.orderNo ? ` · ${row.orderNo}` : ''}
        </span>
      </span>
      <span className="block text-[13px] font-semibold leading-snug !text-ink-primary">{row.issue}</span>
      <span className="block text-[12.5px] leading-snug text-ink-secondary">{row.what}</span>
      {row.reduceImpact ? (
        <span className="line-clamp-2 whitespace-normal text-[12.5px] leading-snug text-ink-primary" title={row.reduceImpact}>
          <span className="text-xs text-muted">to reduce impact</span>
          {` · ${row.reduceImpact}`}
        </span>
      ) : null}
    </TD>
  );
}

function ThreeWRegisterTable({ areas }: { areas: readonly { area: string; entries: readonly ThreeWEntry[] }[] }) {
  const ranks = registerRankCounts();
  return (
    <DowntimeCard
      title="Next 24 hours — the 3W register"
      meta={`by process area · bottleneck first · ${ranks.critical} critical · ${ranks.high} high`}
      className="px-0 pb-0 pt-3 [&>header]:px-4"
    >
      <ScrollCue maxHeight="600px" className="border-t border-[var(--hairline)]">
        <div className={cn(CARD_TABLE_DENSE, '[&_td:first-child]:pl-4 [&_th:first-child]:pl-4 [&_td:last-child]:pr-4 [&_th:last-child]:pr-4')}>
          <Table wrap={false} className="tabular-nums">
            <THead>
              <TR>
                <TH>Issue / risk · what</TH>
                <TH className="whitespace-nowrap">S · P · R</TH>
                <TH>Who</TH>
                <TH className="whitespace-nowrap">Commit</TH>
                <TH className="whitespace-nowrap">Esc.</TH>
                <TH className="whitespace-nowrap">CCC</TH>
                <TH className="whitespace-nowrap">Source</TH>
              </TR>
            </THead>
            <TBody>
              {areas.map((group, groupIndex) => (
                <Fragment key={group.area}>
                  <TR className="bg-[var(--surface-1)]">
                    <TD colSpan={7} className="!py-1 text-[10px] font-semibold uppercase tracking-[0.07em] !text-muted">
                      {group.area}
                      {groupIndex === 0 ? <span className="ml-2 font-medium normal-case tracking-normal">bottleneck</span> : null}
                    </TD>
                  </TR>
                  {group.entries.map((row) => (
                    <TR key={row.id} data-register-row={row.id}>
                      <IssueCell row={row} />
                      <TD className="align-top">
                        <SprTicks row={row} />
                      </TD>
                      <TD className="align-top">
                        <ActorChip actor="Human">{row.who}</ActorChip>
                      </TD>
                      <TD className="whitespace-nowrap align-top !text-ink-primary">
                        {formatCommitDate(row.originalCommit)}
                        {row.currentCommit && row.currentCommit !== row.originalCommit ? (
                          <span className="text-muted"> → {formatCommitDate(row.currentCommit)}</span>
                        ) : null}
                        <CommitStatus row={row} />
                      </TD>
                      <TD className="align-top">
                        <EscalatedCell row={row} />
                      </TD>
                      <TD className="align-top">
                        <CccCell row={row} />
                      </TD>
                      <TD className="min-w-[4.5rem] max-w-[6.5rem] align-top text-xs leading-snug !text-muted">{row.source}</TD>
                    </TR>
                  ))}
                </Fragment>
              ))}
            </TBody>
          </Table>
        </div>
      </ScrollCue>
    </DowntimeCard>
  );
}

// --- Shift handover (Tier 1) -----------------------------------------------

function ShiftHandoverTab() {
  const { state } = useScenario();
  const view = handoverForShift(state);
  const crew = handoverCrew(state);
  const tally = controlCheckTally(view.checks);

  return (
    <div className="space-y-3">
      <div className="grid min-w-0 gap-3 xl:grid-cols-12 xl:items-start">
        <div className="min-w-0 space-y-3 xl:col-span-8">
          <DowntimeCard
            title="Safety first"
            meta={`C crew day shift · from 08 Jun 06:00 · carried from ${crew.label}${
              crew.shortStaffed ? ` (${crew.headcount} of 6, ${crew.seniors} senior — short-staffed)` : ''
            }`}
            className="px-0 pb-0 pt-3 [&>header]:px-4"
          >
            <div className={cn(CARD_TABLE, 'border-t border-[var(--hairline)] [&_td:first-child]:pl-4 [&_td:last-child]:pr-4')}>
              <Table wrap={false}>
                <TBody>
                  {view.safetyItems.map((item) => (
                    <TR key={item.id}>
                      <TD className="w-32 whitespace-nowrap font-semibold !text-ink-primary">{item.kind}</TD>
                      <TD>{item.detail}</TD>
                      <TD className="text-right">
                        <Pill tone={item.priority === 'P1' ? 'danger' : item.priority === 'P2' ? 'warning' : 'default'} className={PILL_SM}>
                          {item.priority}
                        </Pill>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </DowntimeCard>

          <DowntimeCard
            title="3Ws carried into this shift"
            meta={
              <>
                <span className="font-semibold text-ink-secondary">{view.carried.length}</span> open · register order
              </>
            }
            className="px-0 pb-0 pt-3 [&>header]:px-4"
          >
            <ScrollCue maxHeight="250px" className="border-t border-[var(--hairline)]">
              <div className={cn(CARD_TABLE, '[&_td]:px-1.5 [&_th]:px-1.5 [&_td:first-child]:pl-4 [&_th:first-child]:pl-4 [&_td:last-child]:pr-4 [&_th:last-child]:pr-4')}>
                <Table wrap={false} className="tabular-nums">
                  <THead>
                    <TR>
                      <TH>3W</TH>
                      <TH>Issue / risk</TH>
                      <TH>Area</TH>
                      <TH>Who</TH>
                      <TH className="whitespace-nowrap">Commit</TH>
                      <TH className="whitespace-nowrap">Esc.</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {view.carried.map((row) => (
                      <TR key={row.id}>
                        <TD className="whitespace-nowrap font-mono text-xs !text-muted">{row.id}</TD>
                        <TD className="min-[1440px]:min-w-[342px] font-semibold !text-ink-primary">{row.issue}</TD>
                        <TD className="min-[1440px]:min-w-[133px] !text-muted">{row.area}</TD>
                        <TD>
                          <ActorChip actor="Human">{row.who}</ActorChip>
                        </TD>
                        <TD className="whitespace-nowrap !text-ink-primary">{formatCommitDate(row.currentCommit)}</TD>
                        <TD>
                          <EscalatedCell row={row} />
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
            </ScrollCue>
          </DowntimeCard>
        </div>

        <div className="min-w-0 space-y-3 xl:col-span-4">
          <DowntimeCard title="Last 12 hours against plan" meta={view.plan.windowLabel}>
            <dl className="tabular-nums">
              {view.plan.rows.map((row) => (
                <div key={row.id} className="flex items-baseline justify-between gap-3 border-b border-divider py-1.5 text-[13px] last:border-b-0">
                  <dt className="text-ink-secondary">{row.label}</dt>
                  <dd className="whitespace-nowrap text-right">
                    <span
                      className="font-semibold"
                      style={{ color: row.tone === 'ok' ? 'var(--ink-primary)' : vizToneColor(row.tone) }}
                    >
                      {row.unit === '%' ? `${row.actual.toFixed(1)}%` : `${row.actual.toLocaleString()} ${row.unit}`}
                    </span>
                    <span className="text-xs text-muted">
                      {' · '}
                      {row.targetLabel} {row.unit === '%' ? `${row.target.toFixed(1)}%` : `${row.target.toLocaleString()} ${row.unit}`}
                    </span>
                  </dd>
                </div>
              ))}
            </dl>
          </DowntimeCard>

          <DowntimeCard title="Maintenance tasks this shift" className="px-0 pb-1 pt-3 [&>header]:px-4">
            <ul className="border-t border-[var(--hairline)]">
              {view.tasks.map((task) => (
                <li key={task.id} className="flex items-center justify-between gap-3 border-b border-divider px-4 py-1.5 last:border-b-0">
                  <span className="min-w-0">
                    <span className="block font-mono text-xs text-ink-primary">{task.id}</span>
                    <span className="block text-[13px] text-ink-primary">{task.detail}</span>
                  </span>
                  <ActorChip actor="Human">{task.assigned}</ActorChip>
                </li>
              ))}
            </ul>
          </DowntimeCard>
        </div>
      </div>

      <DowntimeCard
        title="Field spot-check results"
        meta={tally.label}
        className="px-0 pb-1 pt-3 [&>header]:px-4"
      >
        <ul className="grid border-t border-[var(--hairline)] px-4 md:grid-cols-3 md:gap-x-5">
          {view.checks.map((check) => (
            <li
              key={check.id}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2.5 border-b border-divider py-1.5 md:[&:nth-last-child(-n+3)]:border-b-0"
            >
              <span className="min-w-0">
                <span className="font-mono text-xs text-ink-primary">{check.id}</span>{' '}
                <span className="font-mono text-xs text-muted">{check.interlockRef}</span>
                <span className="block truncate text-xs text-ink-secondary" title={check.control}>
                  {check.control}
                </span>
              </span>
              <Pill
                tone={
                  check.result === 'pass'
                    ? check.onTime
                      ? 'success'
                      : 'warning'
                    : check.result === 'degraded'
                      ? 'warning'
                      : 'danger'
                }
                className={PILL_SM}
              >
                {check.result === 'pass'
                  ? check.onTime
                    ? 'Verified — pass'
                    : 'Verified — late'
                  : check.result === 'degraded'
                    ? 'Degraded'
                    : 'Fail'}
              </Pill>
            </li>
          ))}
        </ul>
      </DowntimeCard>
    </div>
  );
}

// --- CCC review (Tier 3) -----------------------------------------------

function cccStatusTone(status: CCCCase['status']): 'default' | 'danger' | 'success' | 'warning' {
  switch (status) {
    case 'Overdue':
      return 'danger';
    case 'Recurred — reopened':
      return 'warning';
    case 'Closed — held':
      return 'success';
    default:
      return 'default';
  }
}

function cccStatusLabel(status: CCCCase['status']): string {
  switch (status) {
    case 'Overdue':
      return 'Overdue';
    case 'Recurred — reopened':
      return 'Reopened';
    case 'Closed — held':
      return 'Held';
    default:
      return 'Open';
  }
}

function CccReviewTab() {
  const view = cccReview();
  const band = cccBand();

  return (
    <div className="space-y-3">
      <GlassCard
        as="section"
        tone="matte"
        aria-label={`Monthly review · ${view.periodLabel}`}
        className="overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0"
      >
        <div className="grid grid-cols-2 xl:grid-cols-4">
          {band.map((cell) => (
            <div
              key={cell.id}
              data-band-cell={cell.id}
              className="relative flex min-w-0 flex-col gap-1 border-b border-r border-[var(--hairline)] px-4 pb-2.5 pt-3 last:border-r-0 xl:border-b-0"
            >
              <span aria-hidden className="absolute inset-x-0 top-0 h-[3px]" style={{ backgroundColor: vizToneColor(cell.tone) }} />
              <span className="truncate text-xs font-semibold uppercase tracking-[0.07em] text-muted">{cell.label}</span>
              <span className="text-2xl font-semibold leading-none tabular-nums text-ink-primary">{cell.value}</span>
              <span className="truncate text-xs text-muted">{cell.comparison}</span>
            </div>
          ))}
        </div>
      </GlassCard>

      <DowntimeCard
        title={`CCC case register · ${view.periodLabel}`}
        meta="overdue on review date first"
        actions={
          <ProvenanceChip
            text="[syn]"
            title="Illustrative CCC register — six cases seeded against the daily 3W register; a live site instance reads this from the site's CCC tracking sheet"
            compact
          />
        }
        className="px-0 pb-0 pt-3 [&>header]:px-4"
      >
        <div className={cn(CARD_TABLE, 'border-t border-[var(--hairline)] [&_td:first-child]:pl-4 [&_th:first-child]:pl-4 [&_td:last-child]:pr-4 [&_th:last-child]:pr-4')}>
          <Table wrap={false} className="tabular-nums">
            <THead>
              <TR>
                <TH>Case</TH>
                <TH>3W</TH>
                <TH>Concern</TH>
                <TH>Category</TH>
                <TH>Lead · root-cause review</TH>
                <TH>Stage</TH>
                <TH className="whitespace-nowrap">Review by</TH>
                <TH className="whitespace-nowrap">Cycle</TH>
                <TH>Status</TH>
              </TR>
            </THead>
            <TBody>
              {view.cases.map((row) => (
                <TR key={row.id}>
                  <TD className="whitespace-nowrap font-mono text-xs !text-ink-primary">{row.id}</TD>
                  <TD className="whitespace-nowrap font-mono text-xs !text-muted">{row.sourceThreeWId ?? '—'}</TD>
                  <TD className="min-w-[180px] font-semibold !text-ink-primary">{row.concern}</TD>
                  <TD>{row.failureCategory}</TD>
                  <TD className="whitespace-nowrap">
                    <ActorChip actor="Human">{row.lead}</ActorChip>
                    <div className="mt-0.5 text-xs text-muted">{rootCauseReviewerLabel(row.routedTo)}</div>
                  </TD>
                  <TD>{row.stage}</TD>
                  <TD className="whitespace-nowrap !text-ink-primary">{formatCommitDate(row.committedReview)}</TD>
                  <TD className="whitespace-nowrap">
                    {row.cycleCount} / 10
                    {row.recurred ? <span className="ml-1.5 text-xs text-muted">recurred</span> : null}
                  </TD>
                  <TD className="whitespace-nowrap">
                    <Pill tone={cccStatusTone(row.status)} className={PILL_SM}>
                      {cccStatusLabel(row.status)}
                    </Pill>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      </DowntimeCard>
    </div>
  );
}
